import { userAtom } from "./users-atoms";
import { atom } from "jotai";
import { PlanFeatureTypes, PlanFeature, FEATURE_TYPE_MAP } from "shared/datamodel/schemas";
import { decryptData } from "shared/util/encryption-utils";

// Parse feature value based on the expected type from FeatureTypes
function parseFeatureValue<T extends keyof PlanFeatureTypes>(
  featureName: T,
  value: string | null | undefined
): PlanFeatureTypes[T] | null {
  if (value === null || value === undefined) {
    return null;
  }

  const expectedType = FEATURE_TYPE_MAP[featureName];

  switch (expectedType) {
    case "boolean": {
      return (value === "true") as PlanFeatureTypes[T];
    }
    case "number": {
      const numberValue = Number(value);
      return (Number.isNaN(numberValue) ? null : numberValue) as PlanFeatureTypes[T];
    }
    case "object": {
      try {
        return JSON.parse(value) as PlanFeatureTypes[T];
      } catch {
        return null;
      }
    }
    default: {
      // For string or unknown types, return as is
      return value as unknown as PlanFeatureTypes[T];
    }
  }
}

// 1. Create a base atom for the encrypted features
export const encryptedFeaturesAtom = atom((get) => {
  const user = get(userAtom);
  return user?.planInfo?.enc_features ?? null;
});

// 2. Create an atom for the decryption key
export const featureDecryptionKeyAtom = atom((get) => {
  const user = get(userAtom);
  return user ? user.id + user.account?.id : null;
});

// 3. Create an atom for decrypted features
export const decryptedFeaturesAtom = atom((get) => {
  const encFeatures = get(encryptedFeaturesAtom);
  const decryptionKey = get(featureDecryptionKeyAtom);

  if (!encFeatures || !decryptionKey) {
    return null;
  }

  const decrypted = decryptData(encFeatures, decryptionKey);
  if (!decrypted) {
    return null;
  }

  try {
    return JSON.parse(decrypted) as PlanFeature[];
  } catch {
    return null;
  }
});

// 4. Finally, create the features map atom
export const userPlanFeaturesAtom = atom((get) => {
  const features = get(decryptedFeaturesAtom);
  if (!features) {
    return new Map<keyof PlanFeatureTypes, PlanFeatureTypes[keyof PlanFeatureTypes] | null>();
  }

  return features.reduce((map, feature) => {
    const featureName = feature.name as keyof PlanFeatureTypes;
    map.set(featureName, parseFeatureValue(featureName, feature.feature_value));
    return map;
  }, new Map<keyof PlanFeatureTypes, PlanFeatureTypes[keyof PlanFeatureTypes] | null>());
});
